// api/admin/users.ts
import { API_BASE, getAuthHeaders } from '../config';

export type UserRole = 'admin' | 'user' | 'worker' | 'guest';
export const ADMIN_ALLOWED_ROLES: UserRole[] = ['admin', 'user', 'worker', 'guest'];

export type PlanType = 'BASIC' | 'STANDARD' | 'FAMILY';

export type AdminSubscription = {
    plan_type: PlanType | null;
    is_active: boolean;
    max_profiles?: 1 | 2 | 4 | null;
    started_at?: string | null;
    expires_at?: string | null;
    payment_status?: 'PENDING' | 'COMPLETED' | 'FAILED' | null;
} | null;

export type AdminPremiumState = {
    is_premium: boolean;
    premium_expires_at: string | null;
    premium_source: 'manual' | 'revenuecat' | null;
    manual_premium_expires_at: string | null;
    rc_entitlement_expires_at: string | null;
    rc_app_user_id: string | null;
    is_premium_raw?: boolean;
};

export type AdminUser = {
    id: string;
    email: string;
    role: UserRole | 'moderator' | string;
    is_premium: boolean;              // effective premium z premium_expires_at
    is_premium_raw?: boolean;         // surowa flaga z DB (debug)
    is_verified?: boolean;
    last_active?: string | null;
    created_at: string;
    subscription?: AdminSubscription;
    premium?: AdminPremiumState;
    accepted_latest_legal?: boolean;
    legal_accepted_at?: string | null;
    accepts_email_notifications?: boolean;
    accepts_local_notifications?: boolean;
};

export async function adminFetchUsers(params?: {
    query?: string;
    page?: number;
    limit?: number;
    premium?: 'any' | 'true' | 'false';
    role?: 'any' | UserRole;
    inactive_gt_days?: number;
}) {
    const qp = new URLSearchParams();
    if (params?.query) qp.set('query', params.query);
    if (params?.page) qp.set('page', String(params.page));
    if (params?.limit) qp.set('limit', String(params.limit));
    if (params?.premium) qp.set('premium', params.premium);
    if (params?.role) qp.set('role', params.role);
    if (params?.inactive_gt_days != null) qp.set('inactive_gt_days', String(params.inactive_gt_days));

    const res = await fetch(`${API_BASE}/admin/users?${qp.toString()}`, {
        headers: getAuthHeaders(),
    });
    if (!res.ok) throw await res.json();
    return res.json() as Promise<{ items: AdminUser[]; total: number; page: number; limit: number }>;
}

export async function adminUpdateUserRole(userId: string | number, role: UserRole) {
    const res = await fetch(`${API_BASE}/admin/users/${userId}/role`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ role }),
    });
    if (!res.ok) throw await res.json();
    return res.json() as Promise<{ ok: true; id: number; role: UserRole; unchanged?: boolean }>;
}

export async function adminUpdateUserPremium(
    userId: string,
    payload: {
        action: 'grant' | 'extend' | 'revoke';
        months?: number;
        plan_type?: PlanType;
    }
) {
    const res = await fetch(`${API_BASE}/admin/users/${userId}/premium`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload),
    });
    if (!res.ok) throw await res.json();
    return res.json();
}

/* ========= NOWE helpery (admin) ========= */

export type LearningGoal =
  | 'work'
  | 'travel'
  | 'study'
  | 'move_abroad'
  | 'fun'
  | 'speaking';

export type AdminProfile = {
    id: number;
    user_id?: number;
    name: string;
    age: number;
    is_main: boolean;
    avatar_url?: string | null;
    birth_date?: string | null;           
    learning_level?: number | null;         
    learning_goal?: LearningGoal | null;    
    daily_minutes?: number | null;
    learning_goals?: LearningGoal[];
    interest_category_ids?: number[];
    created_at?: string | null;
};

export async function adminFetchUserProfiles(userId: string | number) {
    const res = await fetch(`${API_BASE}/admin/users/${userId}/profiles`, {
        headers: getAuthHeaders(),
    });
    if (!res.ok) throw await res.json();
    return res.json() as Promise<{ items: AdminProfile[] }>;
}

export type AdminUserLegal = {
    accepted_latest: boolean;
    accepted_at: string | null;
    required_terms_id?: number;
    required_privacy_id?: number;
};

export async function adminFetchUserLegal(userId: string | number) {
    const res = await fetch(`${API_BASE}/admin/users/${userId}/legal`, {
        headers: getAuthHeaders(),
    });
    if (!res.ok) throw await res.json();
    return res.json() as Promise<AdminUserLegal>;
}